import { Tick, ColorSwatch } from '@/components/icons';
import CollapsibleSection from '@/components/ui/collapsible-section';
import { Label } from '@/components/ui/label';
import { PRESET_COLORS, PRESET_COLOR_GROUPS } from '../constants';
import { summarizeColors } from '../summarize';
import { ColorUsageList } from './color/ColorUsageList';
import type { ColorTarget, PresetColors } from '../types';
import type { DividerCustomizations, TableCustomizations } from '@/components/preview/types';

interface ColorSectionProps {
    open: boolean;
    onToggle: () => void;
    localPrimary: string | undefined;
    localSecondary: string | undefined;
    setLocalPrimary: (color: string) => void;
    setLocalSecondary: (color: string) => void;
    onPresetClick: (preset: PresetColors) => void;
    tableConfig: TableCustomizations;
    updateTableConfig: (updates: Partial<TableCustomizations>) => void;
    dividerConfig: DividerCustomizations;
    updateDividerConfig: (updates: Partial<DividerCustomizations>) => void;
    coverOverridesColors: boolean;
    colorTarget: ColorTarget;
    onColorTargetChange: (target: ColorTarget) => void;
}

/**
 * The single home for colour. The palette defines the brand; the usage list below
 * it holds every override that used to be scattered across the Tabela and Divisor
 * sections, each labelled by what it paints.
 */
export function ColorSection({
    open,
    onToggle,
    localPrimary,
    localSecondary,
    setLocalPrimary,
    setLocalSecondary,
    onPresetClick,
    tableConfig,
    updateTableConfig,
    dividerConfig,
    updateDividerConfig,
    coverOverridesColors,
    colorTarget,
    onColorTargetChange,
}: ColorSectionProps) {
    return (
        <CollapsibleSection
            title="Cores"
            testId="customize-section-colors"
            icon={ColorSwatch}
            summary={summarizeColors(localPrimary, localSecondary)}
            open={open}
            onToggle={onToggle}
        >
            {/* Preset Colors, one labelled row per family */}
            <div className="space-y-3">
                {PRESET_COLOR_GROUPS.map((group) => (
                    <div key={group} className="space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                            {PRESET_COLORS.filter((preset) => preset.group === group).map(
                                (preset) => {
                                    const selected =
                                        localPrimary === preset.primary &&
                                        localSecondary === preset.secondary;

                                    return (
                                        <button
                                            key={preset.name}
                                            onClick={() => onPresetClick(preset)}
                                            className={`relative rounded-lg border-2 p-3 transition-all ${
                                                selected
                                                    ? 'border-[#00006B]'
                                                    : 'border-border hover:border-border-strong'
                                            }`}
                                        >
                                            <div className="mb-1.5 flex gap-1">
                                                <div
                                                    className="h-5 flex-1 rounded"
                                                    style={{
                                                        backgroundColor: preset.primary,
                                                    }}
                                                />
                                                <div
                                                    className="h-5 flex-1 rounded"
                                                    style={{
                                                        backgroundColor: preset.secondary,
                                                    }}
                                                />
                                            </div>
                                            <div className="text-xs font-medium text-foreground">
                                                {preset.name}
                                            </div>
                                            {selected && (
                                                <div className="absolute right-1 top-1 rounded-full bg-[#00006B] p-0.5 text-white">
                                                    <Tick className="size-3" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                },
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Custom Colors */}
            <div className="space-y-2">
                <Label className="text-sm font-medium">Cores Customizadas</Label>
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3">
                    <div className="space-y-2">
                        <div className="flex flex-col items-center gap-2">
                            <input
                                type="color"
                                value={localPrimary ?? '#000000'}
                                onChange={(e) => setLocalPrimary(e.target.value)}
                                className="h-16 w-full cursor-pointer rounded-lg border border-border"
                            />
                            <span className="font-mono text-xs text-muted-foreground">
                                Primária: {localPrimary}
                            </span>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="flex flex-col items-center gap-2">
                            <input
                                type="color"
                                value={localSecondary ?? '#000000'}
                                onChange={(e) => setLocalSecondary(e.target.value)}
                                className="h-16 w-full cursor-pointer rounded-lg border border-border"
                            />
                            <span className="font-mono text-xs text-muted-foreground">
                                Secundária: {localSecondary}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <ColorUsageList
                localPrimary={localPrimary}
                localSecondary={localSecondary}
                tableConfig={tableConfig}
                updateTableConfig={updateTableConfig}
                dividerConfig={dividerConfig}
                updateDividerConfig={updateDividerConfig}
                coverOverridesColors={coverOverridesColors}
                target={colorTarget}
                onTargetChange={onColorTargetChange}
            />
        </CollapsibleSection>
    );
}
